import { Injectable, inject } from '@angular/core';
import { AuthService } from '@core/services/auth-service';
import { LocationService } from '@core/services/location-service/location-service';
import { CountryService } from '@core/services/country-service/country-service';
import { BehaviorSubject } from 'rxjs';
import { take } from 'rxjs/operators';
import { Location } from '@moon-v2/moon-share';

@Injectable({
  providedIn: 'root',
})
export class UserConnectedLocationService {
  private readonly authService = inject(AuthService);
  private readonly locationService = inject(LocationService);
  private readonly countrySelectedService = inject(CountryService);

  private userCountrySubject = new BehaviorSubject<Location | null>(null);
  USER_COUNTRY$ = this.userCountrySubject.asObservable();
  private lastUserLocationUuid: string | null = null; // Cache du dernier UUID de location

  initializeUserLocation(): Promise<void> {
    return new Promise((resolve, reject) => {
      // Vérifier si la location est déjà présente dans le BehaviorSubject
      if (this.userCountrySubject.getValue()) {
        resolve();
        return;
      }

      // Récupérer le profil utilisateur pour obtenir sa location
      this.authService
        .getUserProfile()
        .pipe(take(1))
        .subscribe({
          next: (user) => {
            if (user && user.location && user.location !== this.lastUserLocationUuid) {
              // Mettre à jour le cache de l'utilisateur
              this.lastUserLocationUuid = user.location;

              // Chaîne complète des ancêtres (1er élément = pays racine level 0, dernier =
              // la location de l'utilisateur elle-même) — nécessaire car `selectedCountry`
              // sert aussi à OptionsStore pour la config admin (LOCATION_LEVEL_N_NAME...),
              // configurée au niveau pays uniquement. `userLocationChain` (non remontée) porte
              // le scope RBAC réel pour les requêtes de données, et permet de pré-remplir/
              // verrouiller les niveaux parents dans le panneau de filtre carte.
              this.locationService.getAllParentLocations(user.location).subscribe({
                next: (chain) => {
                  const country = chain[0] ?? chain[chain.length - 1] ?? null;
                  if (!country) {
                    console.error('Impossible de résoudre la location de l’utilisateur', {
                      uuid: user.location,
                    });
                    resolve();
                    return;
                  }
                  this.userCountrySubject.next(country);
                  this.countrySelectedService.setCountry(country);
                  this.countrySelectedService.setUserLocationChain(chain);
                  resolve();
                },
                error: (err) => {
                  console.error('Erreur lors de la récupération de la location:', err);
                  reject(err);
                },
              });
            } else {
              resolve();
            }
          },
          error: (err) => {
            console.error('Erreur lors de la récupération du profil utilisateur:', err);
            reject(err);
          },
        });
    });
  }
}
